// src/hooks/useAuth.ts
import { useState, useEffect, useCallback } from 'react'

const API_URL = process.env.REACT_APP_API_URL || ''

function getCookie(name: string): string | null {
  const cookies = document.cookie.split('; ')
  const cookie = cookies.find((c) => c.startsWith(name + '='))
  return cookie ? decodeURIComponent(cookie.substring(name.length + 1)) : null
}

function setCookie(name: string, value: string, domain: string, days: number) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString()
  const secure = domain === 'localhost' ? '' : 'Secure; '
  document.cookie = `${name}=${encodeURIComponent(value)}; domain=${domain}; expires=${expires}; path=/; ${secure}SameSite=Lax`
}

interface AuthState {
  isAuthenticated: boolean
  isLoading: boolean
  token: string | null
}

interface LoginResult {
  success: boolean
  error?: string
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    token: null,
  })

  useEffect(() => {
    const pToken = getCookie('pToken')
    if (!pToken) {
      setState({ isAuthenticated: false, isLoading: false, token: null })
      return
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 5000)

    const baseUrl = API_URL
    fetch(`${baseUrl}/api/professionals/me/plan`, {
      headers: { Authorization: `Bearer ${pToken}` },
      signal: controller.signal,
    })
      .then((res) => {
        clearTimeout(timeout)
        if (res.ok) {
          setState({ isAuthenticated: true, isLoading: false, token: pToken })
        } else {
          setState({ isAuthenticated: false, isLoading: false, token: null })
        }
      })
      .catch(() => {
        clearTimeout(timeout)
        setState({ isAuthenticated: false, isLoading: false, token: null })
      })

    return () => {
      clearTimeout(timeout)
      controller.abort()
    }
  }, [])

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      try {
        const baseUrl = API_URL
        const res = await fetch(`${baseUrl}/public/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: email, password }),
        })

        if (!res.ok) {
          if (res.status === 401) {
            return { success: false, error: 'E-mail ou senha incorretos.' }
          }
          return { success: false, error: 'Erro ao fazer login. Tente novamente.' }
        }

        const data = await res.json()
        const newToken = data?.token?.token
        if (!newToken || typeof newToken !== 'string') {
          return { success: false, error: 'Resposta inesperada do servidor.' }
        }

        const domain =
          window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
            ? 'localhost'
            : 'testfy.com.br'
        setCookie('pToken', newToken, domain, 1)

        setState({ isAuthenticated: true, isLoading: false, token: newToken })
        return { success: true }
      } catch {
        return { success: false, error: 'Erro de conexão. Tente novamente.' }
      }
    },
    []
  )

  return { ...state, login }
}
